import React, { useEffect, useState } from 'react';
import classes from './call-panel.module.scss';
import { JsSipController, JsSipControllerListener } from '../../controllers/jssip-controller';
import Pad from './pad/pad';
import { ReactComponent as PhoneDownIcon } from '../../img/phone-down.svg';
import { ReactComponent as MicrophoneIcon } from '../../img/microphone.svg';
import { ReactComponent as MicrophoneDisabledIcon } from '../../img/microphone-disabled.svg';
import { ReactComponent as SpeakerIcon } from '../../img/speaker.svg';
import { ReactComponent as SpeakerDisabledIcon } from '../../img/speaker-disabled.svg';
import { ReactComponent as KeyboardIcon } from '../../img/keyboard.svg';
import { ReactComponent as KeyboardDisabledIcon } from '../../img/keyboard-disabled.svg';
import cn from 'classnames';
import { CallStatus } from '../../types/enums';
import { formatDuration } from '../../utils/utils';
import { isMobile } from 'react-device-detect';
import LogCollector from '../../controllers/log-collector';

interface CallWrapperProps {
  logCollector: LogCollector;
  jsSipController: JsSipController;
  onEnd?: () => void;
  setIsError: (value: boolean) => void;
}

const padButtons = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

const CallPanel = ({
  logCollector,
  jsSipController,
  onEnd,
  setIsError,
}: CallWrapperProps) => {
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isSpeakerMuted, setIsSpeakerMuted] = useState(false);
  const [isPadOpened, setIsPadOpened] = useState(true);
  const [dtmf, setDtmf] = useState('');
  const [status, setStatus] = useState(CallStatus.CONNECTING);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const listener: JsSipControllerListener = {
      onNewDtmf: (tone) => {
        setDtmf((prev) => prev + tone);
      },
      onMicMute: () => {
        setIsMicMuted(true);
      },
      onMicUnmute: () => {
        setIsMicMuted(false);
      },
      onSpeakerMute: (status) => {
        setIsSpeakerMuted(status);
      },
      onStatusUpdate: (status) => {
        setStatus(status);
      },
      onCallDurationUpdate: (duration) => {
        setDuration(duration);
      },
      onEnd: () => {
        onEnd && onEnd();
        logCollector.sendLogs();
      },
      onError: () => {
        setIsError(true);
        logCollector.sendLogs();
      },
    };

    jsSipController.addListener(listener);

    jsSipController.initialize();

    return () => {
      jsSipController.removeListener(listener);
    };

    // eslint-disable-next-line
  }, []);

  return (
    <div className={cn(classes.callPanel, {
      [classes.callPanel_mobile]: isMobile,
      [classes.callPanel_desktop]: !isMobile,
    })}>
      <div className={classes.callPanel__status}>
        {status === CallStatus.CONNECTING && (
          <span className={cn(
            classes.callPanel__status__text,
            classes.callPanel__status__text_connecting,
          )}>
            Соединение
          </span>
        )}
        {status === CallStatus.CONFIRMED && (
          <span className={cn(
            classes.callPanel__status__text,
            classes.callPanel__status__text_confirmed,
          )}>
            Длительность
            <span className={classes.callPanel__status__text__duration}>
              {formatDuration(duration)}
            </span>
          </span>
        )}
      </div>

      <div className={classes.callPanel__bottom}>

        <div className={cn(classes.callPanel__dtmf, {
          [classes.callPanel__dtmf_opened]: isPadOpened,
        })}>
          <div className={classes.callPanel__number}>
            <span className={classes.callPanel__number__text}>
              {dtmf}
            </span>
          </div>

          <Pad
            buttons={padButtons}
            disabled={!isPadOpened}
            onPadButton={(value) => jsSipController.sendDtmf(value)}
          />
        </div>

        <div className={classes.callPanel__buttons}>

          <div className={classes.callPanel__buttonsRow}>
            <button
              className={cn(classes.callPanel__button, {
                [classes.callPanel__button_disabled]: isMicMuted,
              })}
              onClick={() => jsSipController.micMute()}
            >
              {isMicMuted ?
                <MicrophoneDisabledIcon className={classes.callPanel__button__icon}/> :
                <MicrophoneIcon className={classes.callPanel__button__icon}/>
              }
              <span className={classes.callPanel__button__text}>Микрофон</span>
            </button>

            <button
              className={cn(classes.callPanel__button, {
                [classes.callPanel__button_disabled]: isSpeakerMuted,
              })}
              onClick={() => jsSipController.speakerMute(!isSpeakerMuted)}
            >
              {isSpeakerMuted ?
                <SpeakerDisabledIcon className={classes.callPanel__button__icon}/> :
                <SpeakerIcon className={classes.callPanel__button__icon}/>
              }
              <span className={classes.callPanel__button__text}>Динамик</span>
            </button>

            <button
              className={cn(classes.callPanel__button, {
                [classes.callPanel__button_disabled]: !isPadOpened,
              })}
              onClick={() => setIsPadOpened(!isPadOpened)}
            >
              {isPadOpened ?
                <KeyboardIcon className={classes.callPanel__button__icon}/> :
                <KeyboardDisabledIcon className={classes.callPanel__button__icon}/>
              }
              <span className={classes.callPanel__button__text}>Клавиатура</span>
            </button>
          </div>

          <div className={classes.callPanel__buttonsRow}>
            <button
              className={classes.callPanel__terminateButton}
              onClick={() => jsSipController.terminate()}
            >
              <PhoneDownIcon className={classes.callPanel__terminateButton__icon}/>
              <span className={classes.callPanel__terminateButton__text}>Завершить вызов</span>
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CallPanel;